import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero");

  const navLinks = [
    { name: "Beranda", href: "#hero" },
    { name: "Tentang Kami", href: "#about" },
    { name: "Keunggulan", href: "#whyus" },
    { name: "Paket Aqiqah", href: "#pricing" },
    { name: "Testimoni", href: "#testimonials" },
    { name: "FAQ", href: "#faq" },
    { name: "Kontak", href: "#contact" },
  ];

  // 🔸 Smooth scroll & active section detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 30);

      const sections = document.querySelectorAll("section[id]");
      let current = "#hero";
      sections.forEach((section) => {
        const sectionTop = section.offsetTop - 120;
        if (window.scrollY >= sectionTop) {
          current = `#${section.getAttribute("id")}`;
        }
      });
      setActiveSection(current);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 🔹 WhatsApp direct message
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Assalamu’alaikum, saya ingin konsultasi aqiqah/qurban/catering.\n\nNama saya: \nNomor HP: \nKota: \n\nMohon arahkan saya untuk konsultasi lebih lanjut 🙏"
    );
    window.open(`https://wa.me/6287884243038?text=${message}`, "_blank");
  };

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-lg shadow-md border-b border-[#FFD8C8]/50"
          : "bg-white/70 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
        {/* 🔸 Logo */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-2"
        >
          <img
            src="/TOYIBAH_AQIQAH.png"
            alt="Logo Toyibah Aqiqah"
            className={`transition-all duration-500 ${
              scrolled ? "h-7 md:h-8" : "h-8 md:h-10"
            } w-auto`}
            loading="lazy"
          />
          <h1
            className="text-xl md:text-2xl font-extrabold text-[#FF8A65]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Toyibah <span className="text-[#333]">Aqiqah</span>
          </h1>
        </motion.div>

        {/* 🔹 Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link, i) => (
            <a
              key={i}
              href={link.href}
              className={`text-sm font-medium transition-all ${
                activeSection === link.href
                  ? "text-[#FF8A65] font-semibold"
                  : "text-[#333] hover:text-[#FF8A65]"
              }`}
            >
              {link.name}
            </a>
          ))}

          {/* ✅ WhatsApp direct button */}
          <button
            onClick={handleWhatsAppClick}
            className="ml-4 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-5 py-2.5 rounded-full text-sm shadow-md transition-all hover:scale-105 flex items-center gap-2"
          >
            Hubungi Kami
          </button>
        </nav>

        {/* 🔹 Mobile Menu Toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-[#333] focus:outline-none"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* 🔹 Mobile Dropdown */}
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="md:hidden bg-white/95 backdrop-blur-md shadow-lg border-t border-[#FFD8C8]/40"
        >
          <div className="flex flex-col px-6 py-4 space-y-3">
            {navLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                onClick={() => setOpen(false)}
                className={`font-medium text-base transition-all ${
                  activeSection === link.href
                    ? "text-[#FF8A65]"
                    : "text-[#333] hover:text-[#FF8A65]"
                }`}
              >
                {link.name}
              </a>
            ))}

            {/* ✅ WhatsApp button for mobile */}
            <button
              onClick={() => {
                handleWhatsAppClick();
                setOpen(false);
              }}
              className="mt-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold px-5 py-3 rounded-lg text-center shadow-md transition-all"
            >
              Hubungi Kami via WhatsApp
            </button>
          </div>
        </motion.div>
      )}
    </header>
  );
};

export default Header;
